import React, { useState } from "react";
import CardBase from "../../components/cards/CardBase";
import { alpha, makeStyles, Typography } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import useIncidentBodyPartStatsForSites from "../hooks/queries/useIncidentBodyPartStatsForSites";
import { useSelector } from "react-redux";
import { grey } from "@material-ui/core/colors";
import clsx from "clsx";
import { sub } from "date-fns";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  imageWrapper: {
    width: "100%",
    maxWidth: 700,
  },
  bodyBase: {
    color: theme.palette.secondary.main,
    opacity: 0.2,
  },
  statsGroup: {
    cursor: "pointer",
    transform: "scale(0)",
    animation: `$pop 150ms ${theme.transitions.easing.sharp} calc(var(--animation-order) * 120ms)`,
    animationFillMode: "forwards",
    transformOrigin: "center",
    transformBox: "fill-box",
    "&:hover + $tooltip": {
      opacity: 1,
      visibility: "visible",
    },
  },
  svgCircle: {
    fill: theme.palette.primary.main,
  },
  svgLabel: {
    fill: theme.palette.getContrastText(theme.palette.primary.main),
    fontSize: "32px",
  },
  tooltip: {
    opacity: 0,
    visibility: "hidden",
    backgroundColor: alpha(grey[900], 0.85),
    padding: theme.spacing(1),
    borderRadius: "4px",
    color: "white",
    transform: "translateX(-50%)",
    position: "absolute",
    transition: "opacity 200ms ease-out;",
    "&.active": {
      opacity: 1,
      visibility: "visible",
    },
  },
  selected: {
    "&&": {
      backgroundColor: theme.palette.primary.main,
      color: "white",
    },
  },
  rangePicker: {
    marginBottom: theme.spacing(3),
  },
  bodyPartTotalIncidents: {
    color: theme.palette.getContrastText(theme.palette.primary.main)
  },
  "@keyframes pop": {
    "50%": { transform: "scale(1.3)" },
    "100%": { transform: "scale(1)" },
  },
}));

const circleCoordinates = {
  Head: {
    x: 500,
    y: 60,
  },
  Torso: {
    x: 500,
    y: 250,
  },
  Arm: {
    x: 370,
    y: 400,
  },
  Hand: {
    x: 670,
    y: 520,
  },
  Leg: {
    x: 450,
    y: 730,
  },
  Foot: {
    x: 545,
    y: 930,
  },
};

const TOOLTIP_OFFSET = 16;

function IncidentsBodyPartCard() {
  const classes = useStyles();

  const [currentParts, setCurrentParts] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [weekCount, setWeekCount] = useState(-1);
  const [fromDate, setFromDate] = useState();

  const { selectedSite, activeAppId, appSiteIds } = useSelector(
    (state) => state.account
  );
  const bodyPartStats = useIncidentBodyPartStatsForSites({
    fromDate,
    siteExternalIds: selectedSite
      ? [selectedSite.externalId]
      : appSiteIds[activeAppId],
  });

  const onCircleMouseEnter = (parts) => {
    setCurrentParts(parts);
  };

  const onCircleMouseLeave = () => {
    setCurrentParts(null);
  };

  const onCircleMouseMove = ({ pageX: x, pageY: y }) => {
    if (!currentParts) return;

    setMousePos({ x: x, y: y + TOOLTIP_OFFSET });
  };

  const handleTimeframeChange = (event, newWeekCount) => {
    if (newWeekCount) {
      setWeekCount(newWeekCount);
      newWeekCount == -1
        ? setFromDate(null)
        : setFromDate(sub(new Date(), { weeks: newWeekCount }));
    }
  };

  return (
    <CardBase
      title="By Body Part"
      description="Logged incidents categorised by body part."
      isLoading={bodyPartStats.isLoading}
      isFetching={bodyPartStats.isFetching}
      fullHeight
    >
      <ToggleButtonGroup
        className={classes.rangePicker}
        color="primary"
        value={weekCount.toString()}
        exclusive
        size="small"
        onChange={handleTimeframeChange}
      >
        <ToggleButton value="1" classes={{ selected: classes.selected }}>
          7 Days
        </ToggleButton>
        <ToggleButton value="4" classes={{ selected: classes.selected }}>
          4 Weeks
        </ToggleButton>
        <ToggleButton value="26" classes={{ selected: classes.selected }}>
          6 Months
        </ToggleButton>
        <ToggleButton value="52" classes={{ selected: classes.selected }}>
          12 Months
        </ToggleButton>
        <ToggleButton value="-1" classes={{ selected: classes.selected }}>
          All Time
        </ToggleButton>
      </ToggleButtonGroup>
      <div className={classes.container}>
        <div className={classes.imageWrapper}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 1000"
            xmlSpace="preserve"
          >
            <path
              fill="currentColor"
              className={classes.bodyBase}
              d="M505.2 570.3c-1.4 17 10.1 107 16.1 142.3 2.8 16.7 10.6 44 7.8 58.4-4 20.2-5.1 46.2-2.9 61.4 1.4 9.2 5.8 51.8-.5 67.3-3.3 8.2-9.3 50-9.3 50-15.6 39.4-6.8 37.5-6.8 37.5 4.8 5.9 13.1.5 13.1.5 6.3 4 10.7-1 10.7-1 5.4 4.5 11.7-.6 11.7-.6 6.8 3.5 13.1-3 13.1-3 3.9 2 4.9-.5 4.9-.5 11.7-.8-6.5-38.2-6.5-38.2-4.4-33.7 4.3-52.4 4.3-52.4 28.5-84.6 30-107 18.6-138.9-3.2-9.2-4-12.8-2.5-16.8 3.4-9.2.9-46.3 5.1-61 8-28.4 16-100.4 20.1-134 5.6-45.3-19.7-106-19.7-106-5.5-24.7 2.6-112.7 2.6-112.7 11.3 17.6 10.9 48.7 10.9 48.7-1.8 32.6 26.3 82.4 26.3 82.4 13.5 20.6 18.6 40.1 18.6 41.5 0 5.9-1.3 20.3-1.3 20.3l.5 12.5c.2 3.2 2 14.1 1.7 19.4-2.1 32.6 3.1 26.5 3.1 26.5 4.4 0 9.2-26.2 9.2-26.2 0 6.8-1.7 27 2 34.6 4.4 9.1 7.6-1.6 7.6-3.7 1.2-41.5 3.6-30.6 3.6-30.6 2.4 33.7 5.4 41.3 10.8 38.6 4-1.9.3-40.4.3-40.4 6.9 22.8 12.2 26.5 12.2 26.5 11.5 8 4.4-14.2 2.8-18.6-8.5-23.4-8.7-31.5-8.7-31.5 10.6 21 18.6 20.2 18.6 20.2 10.3-3.3-9-33-20.4-47.3-5.8-7.3-13.2-17-15.4-22.7-3.5-9.8-6.2-41.2-6.2-41.2-1.1-37.1-10.2-53.2-10.2-53.2-15.7-25.1-18.6-71.9-18.6-71.9l-.7-79c-5.5-53.9-45.2-54.3-45.2-54.3-40.1-6-45.7-18.9-45.7-18.9-8.5-12.2-3.6-35.7-3.6-35.7 7.1-5.7 9.8-21 9.8-21 11.7-9 11.1-22.1 5.7-22-4.3.1-3.4-3.5-3.4-3.5C556.6 13 504 10 504 10h-8s-52.5 3-45.2 62.2c0 0 1 3.6-3.4 3.5-5.4-.1-5.9 13 5.8 22 0 0 2.7 15.2 9.8 21 0 0 4.9 23.5-3.6 35.7 0 0-5.6 13-45.7 18.9 0 0-39.8.4-45.2 54.3l-.8 79s-2.9 46.8-18.6 71.9c0 0-9.1 16.1-10.2 53.2 0 0-2.7 31.4-6.2 41.2-2.1 5.7-9.6 15.5-15.4 22.7-11.4 14.2-30.7 43.9-20.4 47.3 0 0 8 .8 18.6-20.2 0 0-.2 8-8.7 31.5-1.7 4.3-8.7 26.6 2.7 18.6 0 0 5.3-3.6 12.2-26.5 0 0-3.7 38.5.4 40.4 5.4 2.6 8.3-5 10.7-38.6 0 0 2.5-10.9 3.6 30.6.1 2.1 3.2 12.8 7.6 3.7 3.7-7.6 2-27.8 2-34.6 0 0 4.7 26.2 9.2 26.2 0 0 5.2 6.1 3.1-26.5-.3-5.3 1.5-16.3 1.8-19.4l.5-12.5s-1.3-14.3-1.3-20.3c0-1.5 5.1-21 18.6-41.5 0 0 28.1-49.8 26.3-82.4 0 0-.4-31.1 10.9-48.7 0 0 8 88 2.6 112.7 0 0-25.3 60.7-19.7 106 4.1 33.7 12 105.6 20.1 134 4.2 14.7 1.7 51.8 5.1 61 1.5 4 .7 7.7-2.5 16.8-11.3 31.9-9.9 54.3 18.6 138.9 0 0 8.8 18.7 4.3 52.4 0 0-18.2 37.5-6.5 38.2 0 0 .9 2.5 4.9.5 0 0 6.3 6.5 13.1 3 0 0 6.3 5 11.7.5 0 0 4.3 5 10.6 1 0 0 8.3 5.6 13.2-.5 0 0 8.7 1.9-6.8-37.5 0 0-6-41.8-9.2-50-6.3-15.6-1.8-58.2-.5-67.3 2.2-15.3 1-41.3-2.9-61.4-2.9-14.4 4.9-41.7 7.8-58.4 5.9-35.3 17.5-125.3 16.1-142.3l4.8 1.7c3.2 0 5.3-1.7 5.3-1.7z"
            />
            {bodyPartStats.data?.map((group, index) => (
              <g
                key={group.groupDescription}
                className={classes.statsGroup}
                onMouseMove={onCircleMouseMove}
                onMouseEnter={() => onCircleMouseEnter(group.parts)}
                onMouseLeave={onCircleMouseLeave}
                style={{ "--animation-order": index }}
              >
                <circle
                  fill="currentColor"
                  className={classes.svgCircle}
                  r="34"
                  cx={circleCoordinates[group.groupDescription]?.x}
                  cy={circleCoordinates[group.groupDescription]?.y}
                />
                <text
                  textAnchor="middle"
                  fill="currentColor"
                  className={classes.svgLabel}
                  x={circleCoordinates[group.groupDescription]?.x}
                  y={circleCoordinates[group.groupDescription]?.y + 10 ?? 0}
                >
                  {group.total}
                </text>
              </g>
            ))}
          </svg>
        </div>
      </div>
      <div
        className={clsx(classes.tooltip, { active: !!currentParts })}
        style={{ top: mousePos.y, left: mousePos.x }}
      >
        {currentParts?.map((part) => (
          <Typography key={part.bodyPart} variant="body2" className={classes.bodyPartTotalIncidents}>
            {part.bodyPartDescription}: {part.total}
          </Typography>
        ))}
      </div>
    </CardBase>
  );
}

export default IncidentsBodyPartCard;
